import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../../../firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

const SignInForm: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // 이미 로그인된 사용자 체크
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        navigate('/main');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const updateLastLoginAt = async (userId: string) => {
    try {
      // 사용자 프로필 문서가 존재하는지 확인
      const userProfileRef = doc(db, 'user_profiles', userId);
      const userProfileSnap = await getDoc(userProfileRef);
      
      if (userProfileSnap.exists()) {
        // lastLoginAt 필드 업데이트
        await updateDoc(userProfileRef, {
          lastLoginAt: new Date().toISOString()
        });
        console.log('lastLoginAt 업데이트 완료');
      } else {
        console.warn('사용자 프로필을 찾을 수 없습니다:', userId);
      }
    } catch (error) {
      console.error('lastLoginAt 업데이트 실패:', error);
      // lastLoginAt 업데이트 실패는 로그인 자체를 막지 않도록 함
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email.trim() || !formData.password.trim()) {
      setError('이메일과 비밀번호를 모두 입력해주세요.');
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      // Firebase Auth로 로그인
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // 로그인 성공 시 lastLoginAt 업데이트
      if (userCredential.user) {
        await updateLastLoginAt(userCredential.user.uid);
      }

      // 로그인 성공 시 메인 페이지로 리다이렉트
      navigate('/main', { replace: true });
    } catch (error: any) {
      console.error('로그인 오류:', error);
      let errorMessage = '로그인 중 오류가 발생했습니다.';
      
      // Firebase 오류 메시지 한글화
      switch (error.code) {
        case 'auth/invalid-email':
          errorMessage = '유효하지 않은 이메일 주소입니다.';
          break;
        case 'auth/user-disabled':
          errorMessage = '비활성화된 계정입니다.';
          break;
        case 'auth/user-not-found':
          errorMessage = '등록되지 않은 이메일입니다.';
          break;
        case 'auth/wrong-password':
          errorMessage = '잘못된 비밀번호입니다.';
          break;
        case 'auth/too-many-requests':
          errorMessage = '너무 많은 로그인 시도가 있었습니다. 잠시 후 다시 시도해주세요.';
          break;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-8">
      <div className="space-y-6">
        <div>
          <label htmlFor="email" className="w-[235px] h-[22px] left-[40px] md:left-[74px] top-[160px] md:top-[179px] absolute justify-center text-[#172033] text-[13px] md:text-base font-semibold font-['Pretendard'] leading-[38px]">
            이메일
          </label>
          <div className="w-[calc(100%-80px)] md:w-[540px] h-12 md:h-14 left-[40px] md:left-[74px] top-[188px] md:top-[213px] absolute">
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              className="w-full h-full px-[18px] py-[15px] bg-white rounded-sm outline outline-[1px] md:outline-[0.50px] outline-[#d6dae5] text-[15px] font-medium font-['Pretendard'] leading-relaxed"
              placeholder="이메일 주소를 입력해주세요"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        
        <div>
          <div className="flex justify-between items-center">
            <label htmlFor="password" className="w-[137px] h-[22px] left-[40px] md:left-[74px] top-[259px] md:top-[302px] absolute justify-center text-[#172033] text-[13px] md:text-base font-semibold font-['Pretendard'] leading-[38px]">
              비밀번호
            </label>
            <button
              type="button"
              onClick={() => navigate('/reset-password')}
              className="w-[151px] h-6 left-[calc(100%-192px)] md:left-[470px] top-[263px] md:top-[302px] absolute text-right justify-center text-[#2b3f81] text-[11px] md:text-sm font-medium font-['Pretendard'] leading-relaxed"
            >
              비밀번호를 잊으셨나요?
            </button>
          </div>
          <div className="w-[calc(100%-80px)] md:w-[540px] h-12 md:h-14 left-[40px] md:left-[74px] top-[288px] md:top-[336px] absolute">
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              className="w-full h-full px-[18px] py-[15px] bg-white rounded-sm outline outline-[1px] md:outline-[0.50px] outline-[#d6dae5] text-[15px] font-medium font-['Pretendard'] leading-relaxed"
              placeholder="비밀번호를 입력해주세요"
              value={formData.password}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        {error && (
          <div className="w-[calc(100%-80px)] md:w-[540px] left-[40px] md:left-[74px] top-[352px] md:top-[400px] absolute text-red-600 text-sm font-medium font-['Pretendard']">
            {error}
          </div>
        )}

        <button
          type="submit"
          className={`w-[calc(100%-100px)] md:w-[540px] h-[52px] md:h-16 left-[40px] md:left-[74px] top-[360px] md:top-[421px] absolute rounded-sm ${
            formData.email.trim() && formData.password.trim() 
              ? 'bg-[#155acb]' 
              : 'bg-gray-400'
          } disabled:bg-gray-400 flex items-center justify-center`}
          disabled={loading || !formData.email.trim() || !formData.password.trim()}
        >
          <span className="text-white text-base font-bold font-['Pretendard'] leading-[38px]">
            {loading ? '로그인 중...' : '로그인'}
          </span>
        </button>

        <div className="flex items-center justify-center md:justify-start space-x-2 left-[40px] md:left-[74px] top-[412px] md:top-[505px] absolute w-[calc(100%-100px)] md:w-auto">
          <div className="text-[#2b3a5c] text-[13px] md:text-base font-normal font-['Pretendard'] leading-[38px]">
            계정이 없으신가요?
          </div>
          <button
            type="button"
            onClick={() => navigate('/signup')}
            className="text-[#2e97ee] text-[13px] md:text-base font-medium font-['Pretendard'] underline leading-relaxed"
          >
            회원가입
          </button>
        </div>
      </div>
    </form>
  );
};

export default SignInForm; 